import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

interface SiteSettings {
  mission: string;
  facebook: string;
  tiktok: string;
  instagram: string;
  linkedin: string;
}

export default function Footer() {
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    mission: "",
    facebook: "",
    tiktok: "",
    instagram: "",
    linkedin: "",
  });
  const [subEmail, setSubEmail] = useState("");
  const [subStatus, setSubStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase.from("site_settings").select("key, value");
      const map: Record<string, string> = {};
      (data || []).forEach((s) => { map[s.key] = s.value; });
      setSiteSettings({
        mission: map.mission || "",
        facebook: map.facebook || "",
        tiktok: map.twitter || "",
        instagram: map.instagram || "",
        linkedin: map.linkedin || "",
      });
    };
    fetchSettings();
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subEmail.trim()) return;
    setSubStatus("submitting");
    const { error } = await supabase.from("newsletter_subscribers").insert({ email: subEmail.trim().toLowerCase() });
    if (error) {
      setSubStatus("error");
    } else {
      setSubStatus("success");
      setSubEmail("");
      setTimeout(() => setSubStatus("idle"), 3000);
    }
  };
  const quickLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About Us" },
    { to: "/events", label: "Events" },
    { to: "/gallery", label: "Gallery" },
    { to: "/contact", label: "Contact" },
  ];

  const resourceLinks = [
    { to: "/events", label: "News & Updates" },
    { to: "/about", label: "Our Mission" },
    { to: "/events", label: "Workshops" },
    { to: "/gallery", label: "Photo Gallery" },
    { to: "/contact", label: "Get in Touch" },
  ];

  return (
    <footer className="bg-primary-900 text-white">
      {/* Main Footer */}
      <div className="px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Logo & Description */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://static.readdy.ai/image/cb5bba3f8fb0d3000da0c3fb91699bfa/a250bf31368e0cf0c4367263661697d1.jpeg"
                alt="Logo"
                className="h-12 w-12 rounded-full object-cover"
              />
              <div>
                <p className="font-heading font-bold text-base">
                  ACS Student Chapter
                </p>
                <p className="text-xs text-white/60">Rivers State University</p>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              {siteSettings.mission}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-sm mb-4 text-gold-400">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-white/70 hover:text-gold-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading font-semibold text-sm mb-4 text-gold-400">
              Resources
            </h4>
            <ul className="space-y-2.5">
              {resourceLinks.map((link) => (
                <li key={link.to + link.label}>
                  <Link
                    to={link.to}
                    className="text-sm text-white/70 hover:text-gold-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading font-semibold text-sm mb-4 text-gold-400">
              Stay Updated
            </h4>
            <p className="text-sm text-white/70 mb-4">
              Subscribe for news and event updates.
            </p>
            {subStatus === "success" && (
              <p className="text-xs text-emerald-400 mb-2">Subscribed successfully!</p>
            )}
            {subStatus === "error" && (
              <p className="text-xs text-red-400 mb-2">Already subscribed or invalid email.</p>
            )}
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                name="email"
                value={subEmail}
                onChange={(e) => setSubEmail(e.target.value)}
                placeholder="Your email"
                required
                className="flex-1 px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:border-gold-500 transition-colors"
              />
              <button
                type="submit"
                disabled={subStatus === "submitting"}
                className="px-5 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap disabled:opacity-60"
              >
                {subStatus === "submitting" ? "..." : "Subscribe"}
              </button>
            </form>
            <div className="flex items-center gap-3 mt-5">
              {siteSettings.facebook && (
                <a
                  href={siteSettings.facebook}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-white/30 text-white/70 hover:bg-gold-600 hover:border-gold-600 hover:text-white transition-all"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-fill text-sm" />
                </a>
              )}
              {siteSettings.tiktok && (
                <a
                  href={siteSettings.tiktok}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-white/30 text-white/70 hover:bg-gold-600 hover:border-gold-600 hover:text-white transition-all"
                  aria-label="Twitter"
                >
                  <i className="ri-tiktok-x-fill text-sm" />
                </a>
              )}
              {siteSettings.instagram && (
                <a
                  href={siteSettings.instagram}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-white/30 text-white/70 hover:bg-gold-600 hover:border-gold-600 hover:text-white transition-all"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-line text-sm" />
                </a>
              )}
              {siteSettings.linkedin && (
                <a
                  href={siteSettings.linkedin}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-white/30 text-white/70 hover:bg-gold-600 hover:border-gold-600 hover:text-white transition-all"
                  aria-label="LinkedIn"
                >
                  <i className="ri-linkedin-fill text-sm" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="bg-primary-950 px-4 md:px-8 lg:px-12 py-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>
            &copy; {new Date().getFullYear()} ACS Student Chapter, Rivers State University. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="hover:text-white/80 transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/"
              className="hover:text-white/80 transition-colors"
            >
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}